import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home,
  ShieldCheck,
  Settings,
  Award,
  UserCheck,
  Smile,
  Smartphone,
  MapPin,
  Calendar,
  Lock,
  Users,
  Zap,
  Laptop,
  Monitor,
  Tablet,
  Headphones,
  PhoneCall,
  ArrowRight,
  ChevronRight,
  Cpu,
  Clock,
  CheckCircle2,
  Wrench,
  MousePointerClick,
  Truck,
  CalendarDays,
  Bike
} from 'lucide-react';
import './About.css';

export default function About() {
  return (
    <div className="about-page">
      {/* 1. Hero & Company Overview Section */}
      <section className="about-hero-section">
        <div className="about-container">

          {/* Breadcrumbs */}
          <nav className="about-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <ChevronRight size={14} className="breadcrumb-separator" />
            <span className="breadcrumb-current">About Us</span>
          </nav>

          <div className="about-hero-grid">
            {/* Left Content */}
            <div className="about-hero-content">
              <h1 className="about-main-title">
                About <span className="gold-highlight">DSMR Solutions</span>
              </h1>
              <h2 className="about-subtitle">
                Your Trusted Partner in Device Care
              </h2>
              <p className="about-description">
                At DSMR Solutions, we believe technology makes life better when it works perfectly.
                That's why we bring expert repair services right to your doorstep – saving you time,
                hassle, and unnecessary wait.
              </p>

              {/* Action Buttons Row */}
              <div className="hero-buttons-row">
                <a href="/#booking" className="btn-solid-navy">
                  <div className="btn-icon-circle gold-circle">
                    <ArrowRight size={14} />
                  </div>
                  <span>Book Your Repair</span>
                </a>

                <a href="https://wa.me/918700319769" target="_blank" rel="noopener noreferrer" className="btn-outline-white">
                  <div className="btn-icon-circle navy-circle">
                    <PhoneCall size={14} />
                  </div>
                  <span>Get Free Quote</span>
                </a>
              </div>

              {/* 4 Bottom Stat Cards */}
              <div className="hero-stat-cards-row">
                <div className="hero-stat-card">
                  <div className="stat-card-icon-box">
                    <Wrench size={20} />
                  </div>
                  <div className="stat-card-text">
                    <span className="stat-card-number">15,000+</span>
                    <span className="stat-card-label">Devices Repaired</span>
                  </div>
                </div>

                <div className="hero-stat-card">
                  <div className="stat-card-icon-box">
                    <Users size={20} />
                  </div>
                  <div className="stat-card-text">
                    <span className="stat-card-number">10,000+</span>
                    <span className="stat-card-label">Happy Customers</span>
                  </div>
                </div>

                <div className="hero-stat-card">
                  <div className="stat-card-icon-box">
                    <ShieldCheck size={20} />
                  </div>
                  <div className="stat-card-text">
                    <span className="stat-card-number">1 Year</span>
                    <span className="stat-card-label">Warranty</span>
                  </div>
                </div>

                <div className="hero-stat-card">
                  <div className="stat-card-icon-box">
                    <MapPin size={20} />
                  </div>
                  <div className="stat-card-text">
                    <span className="stat-card-number">Delhi NCR</span>
                    <span className="stat-card-label">Service Area</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Visual Image Frame */}
            <div className="about-hero-visual">
              {/* Soft Ambient Glow */}
              <div className="hero-glow-bg"></div>

              <div className="visual-frame-container">
                <img
                  src="/images/about_team.png"
                  alt="DSMR Solutions Expert Technicians Team"
                  className="hero-team-image"
                />

                {/* 5+ Years Badge Overlay with Gold Shield Icon */}
                <div className="years-badge-modern">
                  <div className="badge-shield-circle">
                    <ShieldCheck size={22} />
                  </div>
                  <div className="badge-content">
                    <span className="badge-number-gold">1</span>
                    <span className="badge-text-white">Year<br />Warranty</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Our Story Section */}
      <section className="about-story-section">
        <div className="about-container">
          <div className="story-layout-grid-two">

            {/* Story Card (Dark Navy Left Box) */}
            <div className="story-navy-card">
              <h2 className="story-title">Our Story</h2>
              <div className="story-content">
                <p>
                  DSMR Solutions was founded with a simple mission – to make professional
                  device repair services convenient, reliable, and transparent for everyone.
                </p>
                <p>
                  What started as a small doorstep service in Delhi NCR has now grown into a
                  trusted brand, helping thousands of happy customers with their smartphones,
                  laptops, tablets, and more.
                </p>
                <p>
                  We understand how important your devices are in your daily life. That's why
                  we combine skilled technicians, genuine parts, and a customer-first approach
                  to deliver a seamless repair experience.
                </p>
              </div>

              {/* Signature Graphic */}
              <div className="story-signature-block">
                <div className="signature-font">DSMR</div>
                <div className="signature-caption">– Team DSMR Solutions</div>
              </div>
            </div>

            {/* Story Image Right Block */}
            <div className="story-image-card">
              <img
                src="/images/about_repair.png"
                alt="Precision Micro Electronics Repair Work"
                className="story-repair-img"
              />
            </div>

          </div>
        </div>
      </section>

      {/* 3. Our Values Section */}
      <section className="about-values-section">
        <div className="about-container">
          <div className="section-header">
            <h2 className="section-title">Our Values</h2>
            <div className="gold-accent-line"></div>
          </div>

          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon-circle">
                <ShieldCheck size={28} className="value-icon" />
              </div>
              <h3 className="value-card-title">Trust & Transparency</h3>
              <p className="value-card-desc">
                We believe in honest communication and clear pricing with no hidden charges.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon-circle">
                <Award size={28} className="value-icon" />
              </div>
              <h3 className="value-card-title">Quality First</h3>
              <p className="value-card-desc">
                We use only genuine parts and follow the highest standards in every repair.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon-circle">
                <Users size={28} className="value-icon" />
              </div>
              <h3 className="value-card-title">Customer First</h3>
              <p className="value-card-desc">
                Your satisfaction is our priority. We go the extra mile to help you.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon-circle">
                <Zap size={28} className="value-icon" />
              </div>
              <h3 className="value-card-title">Convenience</h3>
              <p className="value-card-desc">
                We come to you – saving your time with doorstep repair services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Flow / How It Works Section */}
      <section className="about-process-section">
        <div className="about-container">
          <div className="section-header center-header">
            <h2 className="section-title">Our Doorstep Repair Process</h2>
            <div className="gold-accent-line centered-gold-line"></div>
          </div>

          <div className="process-flow-wrapper">
            <div className="process-flow-row">
              {/* Step 1 */}
              <div className="process-card">
                <div className="card-top-header">
                  <div className="number-circle-badge">01</div>
                  <h3 className="card-title-text">Easy Booking</h3>
                </div>
                <div className="card-bottom-body">
                  <div className="card-navy-icon-box">
                    <CalendarDays size={30} />
                  </div>
                  <p className="card-description-text">
                    Share your device details with us and choose a convenient time for our visit.
                  </p>
                </div>
              </div>

              <div className="dotted-connector-line"></div>

              {/* Step 2 */}
              <div className="process-card">
                <div className="card-top-header">
                  <div className="number-circle-badge">02</div>
                  <h3 className="card-title-text">Doorstep Visit</h3>
                </div>
                <div className="card-bottom-body">
                  <div className="card-navy-icon-box">
                    <Bike size={30} />
                  </div>
                  <p className="card-description-text">
                    Our technician arrives at your location with all the required tools and genuine parts.
                  </p>
                </div>
              </div>

              <div className="dotted-connector-line"></div>

              {/* Step 3 */}
              <div className="process-card">
                <div className="card-top-header">
                  <div className="number-circle-badge">03</div>
                  <h3 className="card-title-text">Instant Repair</h3>
                </div>
                <div className="card-bottom-body">
                  <div className="card-navy-icon-box">
                    <Wrench size={30} />
                  </div>
                  <p className="card-description-text">
                    We diagnose and repair your device on the spot in most cases.
                  </p>
                </div>
              </div>

              <div className="dotted-connector-line"></div>

              {/* Step 4 */}
              <div className="process-card">
                <div className="card-top-header">
                  <div className="number-circle-badge">04</div>
                  <h3 className="card-title-text">Test & Warranty</h3>
                </div>
                <div className="card-bottom-body">
                  <div className="card-navy-icon-box">
                    <ShieldCheck size={30} />
                  </div>
                  <p className="card-description-text">
                    We test thoroughly and provide 1 Year warranty on every repair.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. What We Repair Section */}

      {/* 5. Why Choose DSMR Solutions? Banner */}
      <section className="why-choose-banner-section">
        <div className="about-container">
          <h2 className="banner-title">
            Why Choose <span className="banner-gold">DSMR Solutions?</span>
          </h2>

          <div className="why-choose-grid">
            <div className="why-item">
              <div className="why-icon-box">
                <Home size={26} />
              </div>
              <h4 className="why-item-title">Free Home Visit</h4>
              <p className="why-item-desc">No extra cost for home service.</p>
            </div>

            <div className="why-item">
              <div className="why-icon-box">
                <Wrench size={26} />
              </div>
              <h4 className="why-item-title">Trained Experts</h4>
              <p className="why-item-desc">Skilled & experienced technicians.</p>
            </div>

            <div className="why-item">
              <div className="why-icon-box">
                <Cpu size={26} />
              </div>
              <h4 className="why-item-title">Genuine Parts</h4>
              <p className="why-item-desc">100% original parts for long-lasting performance.</p>
            </div>

            <div className="why-item">
              <div className="why-icon-box">
                <ShieldCheck size={26} />
              </div>
              <h4 className="why-item-title">Up to 1 Year Warranty</h4>
              <p className="why-item-desc">Peace of mind with every repair.</p>
            </div>

            <div className="why-item">
              <div className="why-icon-box">
                <Headphones size={26} />
              </div>
              <h4 className="why-item-title">24/7 Support</h4>
              <p className="why-item-desc">We're always here when you need us.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Call To Action (CTA) Section */}
      <section className="about-cta-section">
        <div className="about-container">
          <div className="cta-box">
            <div className="cta-headset-icon">
              <PhoneCall size={32} />
            </div>

            <div className="cta-text-content">
              <span className="cta-tag">Need Help with Your Device?</span>
              <h3 className="cta-heading">We're Just a Call Away!</h3>
              <p className="cta-subheading">
                Book your repair today and experience the DSMR difference.
              </p>
            </div>

            <a href="/#booking" className="cta-button">
              <span>Book Your Repair Now</span>
              <div className="cta-arrow-circle">
                <ArrowRight size={16} />
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
